"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PlayerAvatar } from "@/components/shared/player-avatar";
import type { VysledokHraca } from "@/types/database";
import { getHracId, getHracMeno, getPozicia, getKlub, getPocetHlasov } from "@/types/database";
import { Shield, Users, Target, Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface RosterFormationProps {
  vysledky: VysledokHraca[];
}

export function RosterFormation({ vysledky }: RosterFormationProps) {
  // Zoskupenie hráčov podľa pozícií (top hráči pre každú pozíciu)
  const brankari = vysledky
    .filter((v) => getPozicia(v) === "brankar")
    .sort((a, b) => getPocetHlasov(b) - getPocetHlasov(a))
    .slice(0, 2);
  const obrancovia = vysledky
    .filter((v) => getPozicia(v) === "obranca")
    .sort((a, b) => getPocetHlasov(b) - getPocetHlasov(a))
    .slice(0, 8);
  const utocnici = vysledky
    .filter((v) => getPozicia(v) === "utocnik")
    .sort((a, b) => getPocetHlasov(b) - getPocetHlasov(a))
    .slice(0, 12);

  const getPoziciaIcon = (pozicia: string) => {
    switch (pozicia) {
      case "brankar":
        return <Shield className="h-4 w-4 text-status-info" />;
      case "obranca":
        return <Users className="h-4 w-4 text-primary" />;
      case "utocnik":
        return <Target className="h-4 w-4 text-status-success" />;
      default:
        return null;
    }
  };

  const PlayerRow = ({
    hrac,
    positionLabel,
    index,
  }: {
    hrac: VysledokHraca;
    positionLabel: string;
    index: number;
  }) => (
    <div
      className={cn(
        "flex flex-col sm:flex-row sm:items-center justify-between",
        "py-2 sm:py-3 border-b border-muted/30 last:border-0 hover:bg-muted/10 px-2 sm:px-3 transition-colors gap-2 sm:gap-3",
      )}
    >
      <div className="flex items-center gap-3">
        <span className="text-xs font-mono text-muted-foreground w-4">
          {index + 1}.
        </span>
        <div className="flex items-center gap-2">
          <PlayerAvatar
            hracId={getHracId(hrac)}
            meno={getHracMeno(hrac)}
            pozicia={getPozicia(hrac)}
            className="h-8 w-8 border border-muted"
            fallbackClassName="text-xs"
          />
          <div className="flex flex-col">
            <span className="text-sm font-semibold leading-none">
              {getHracMeno(hrac)}
            </span>
            <span className="text-xs text-muted-foreground uppercase font-bold">
              {getKlub(hrac) || "Bez klubu"}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-4 ml-7 sm:ml-0">
        <div className="flex flex-col items-end">
          <Badge
            variant="outline"
            className="text-xs py-0 h-5 border-muted/60 text-muted-foreground uppercase tracking-wider"
          >
            {positionLabel}
          </Badge>
          <div className="flex items-center gap-1 mt-0.5">
            <Star className="h-3 w-3 text-hockey-gold fill-hockey-gold" />
            <span className="text-xs font-bold">{getPocetHlasov(hrac)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {/* Brankári */}
      <Card className="border-muted/60 shadow-sm overflow-hidden">
        <CardHeader className="bg-status-info/5 py-3 border-b border-muted/40">
          <div className="flex items-center gap-2">
            {getPoziciaIcon("brankar")}
            <CardTitle className="text-sm font-bold uppercase tracking-wider">
              Brankári
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {brankari.map((h, i) => (
            <PlayerRow
              key={getHracId(h)}
              hrac={h}
              positionLabel="G"
              index={i}
            />
          ))}
          {brankari.length === 0 && (
            <div className="p-4 text-center text-xs text-muted-foreground">
              Žiadni brankári
            </div>
          )}
        </CardContent>
      </Card>

      {/* Obrancovia - Formácia 1 & 2 */}
      <Card className="border-muted/60 shadow-sm overflow-hidden">
        <CardHeader className="bg-primary/5 py-3 border-b border-muted/40">
          <div className="flex items-center gap-2">
            {getPoziciaIcon("obranca")}
            <CardTitle className="text-sm font-bold uppercase tracking-wider">
              Obrancovia
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {obrancovia.map((h, i) => (
            <PlayerRow
              key={getHracId(h)}
              hrac={h}
              positionLabel="D"
              index={i}
            />
          ))}
          {obrancovia.length === 0 && (
            <div className="p-4 text-center text-xs text-muted-foreground">
              Žiadni obrancovia
            </div>
          )}
        </CardContent>
      </Card>

      {/* Útočníci - Formácia 1, 2, 3 */}
      <Card className="border-muted/60 shadow-sm overflow-hidden lg:col-span-1">
        <CardHeader className="bg-status-success/5 py-3 border-b border-muted/40">
          <div className="flex items-center gap-2">
            {getPoziciaIcon("utocnik")}
            <CardTitle className="text-sm font-bold uppercase tracking-wider">
              Útočníci
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {utocnici.map((h, i) => (
            <PlayerRow
              key={getHracId(h)}
              hrac={h}
              positionLabel="F"
              index={i}
            />
          ))}
          {utocnici.length === 0 && (
            <div className="p-4 text-center text-xs text-muted-foreground">
              Žiadni útočníci
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
